import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';

import FixedImg from 'Components/Utility/FixedImg';

function AgeRatings({ ratings, intl, nintendoProduct, slug, showTitle, hitcard }) {
  const { formatMessage } = intl;
  const messages = defineMessages({
    acb: {
      id: 'agerating.acb',
      defaultMessage: 'ACB rating of {rating}',
    },
    esrb: {
      id: 'agerating.esrb',
      defaultMessage: 'ESRB rating of {rating}',
    },
    pegi: {
      id: 'agerating.pegi',
      defaultMessage: 'PEGI rating of {rating}',
    },
    usk: {
      id: 'agerating.usk',
      defaultMessage: 'USK rating of {rating}',
    },
  });

  if (isEmpty(ratings)) return false;
  const requiredSlug = 'fishing-sim-world-pro-tour-quad-lake-pass';

  const acbWhitelist = [0, 1, 3, 6, 15, 16, 18];
  const esrbWhitelist = [0, 3, 6, 10, 13, 17, 18];
  const pegiWhitelist = [0, 3, 7, 12, 16, 18];
  const uskWhitelist = [0, 3, 6, 12, 16, 18];

  // Safety net to make sure violent games always have an 18 rating.
  const ESRB = ratings.ESRB;
  let ACB = ratings.ACB;
  if (ratings.ACB === 17) ACB = 18;
  let PEGI = ratings.PEGI;
  if (ratings.PEGI === 17) PEGI = 18;
  let USK = ratings.USK;
  if (ratings.USK === 17) USK = 18;

  // Dont show the age box if all ratings are either 0 or 20 or are not in
  // the whitelist and not a Nintendo product. We must always show ratings on Nintendo products.
  if (
    (!includes(acbWhitelist, ACB) || ACB === 0 || ACB >= 20) &&
    (!includes(esrbWhitelist, ESRB) || ESRB === 0 || ESRB >= 20) &&
    (!includes(pegiWhitelist, PEGI) || PEGI === 0 || PEGI >= 20) &&
    (!includes(uskWhitelist, USK) || USK === 0 || USK >= 20) &&
    !nintendoProduct &&
    slug !== requiredSlug
  ) {
    return false;
  }

  const imageChecker = (type) => {
    let showImage = false;
    // Override: If Nintendo product AND board is PEGI/USK and they are set to 0 return true
    if (nintendoProduct && ((type === 'PEGI' && PEGI === 0) || (type === 'USK' && USK === 0))) showImage = true;

    // Override: If requiredSlug matches the slug and rating is 0 return true
    if (slug === requiredSlug && ratings[type] === 0) showImage = true;

    // If rating is in the whitelist and not 0 or 20 and above return true
    if (type === 'ACB' && includes(acbWhitelist, ACB) && (ACB !== 0 && !(ACB >= 20))) showImage = true;
    if (type === 'ESRB' && includes(esrbWhitelist, ESRB) && (ESRB !== 0 && !(ESRB >= 20))) showImage = true;
    if (type === 'PEGI' && includes(pegiWhitelist, PEGI) && (PEGI !== 0 && !(PEGI >= 20))) showImage = true;
    if (type === 'USK' && includes(uskWhitelist, USK) && (USK !== 0 && !(USK >= 20))) showImage = true;

    if (showImage) {
      const typeLower = type.toLowerCase();
      let ageNumber = 0;
      if (type === 'ACB') ageNumber = ACB;
      if (type === 'ESRB') ageNumber = ESRB;
      if (type === 'PEGI') ageNumber = PEGI;
      if (type === 'USK') ageNumber = USK;

      return (
        <div className="age-image-container">
          <FixedImg
            fluid
            lazy={false}
            subfolder="age-ratings"
            src={`${type}/${ageNumber}.png`}
            alt={formatMessage(messages[typeLower], { rating: ageNumber })}
            width="50"
          />
        </div>
      );
    }
    return false;
  };

  if (showTitle) {
    return (
      <React.Fragment>
        <hr />
        <article>
          <h4>
            <FormattedMessage id="age-ratings.title" defaultMessage="Age Ratings" />
          </h4>
          <div className={`ageratings-container w-100 ${hitcard ? 'hitcard-bg' : ''}`}>
            <div className="age-grid">
              {imageChecker('ACB')}
              {imageChecker('ESRB')}
              {imageChecker('PEGI')}
              {imageChecker('USK')}
            </div>
          </div>
        </article>
      </React.Fragment>
    );
  }

  return (
    <div className={`ageratings-container w-100 ${hitcard ? 'hitcard-bg' : ''}`}>
      <div className="age-grid">
        {imageChecker('ACB')}
        {imageChecker('ESRB')}
        {imageChecker('PEGI')}
        {imageChecker('USK')}
      </div>
    </div>
  );
}

AgeRatings.propTypes = {
  ratings: PropTypes.shape({}),
  slug: PropTypes.string,
  nintendoProduct: PropTypes.bool,
  showTitle: PropTypes.bool,
  hitcard: PropTypes.bool,
};

AgeRatings.defaultProps = {
  ratings: {},
  slug: '',
  nintendoProduct: false,
  showTitle: false,
  hitcard: false,
};

export default injectIntl(AgeRatings);
